import type { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import type { GyroscopeReading } from '../domain/telemetry/raw/GyroscopeReading'
import {
  GYRO_BIAS_MAX_LEAN_DEG,
  GYRO_BIAS_MAX_TICK_GAP_MS,
  GYRO_BIAS_MIN_SPEED_KMH,
  GYRO_BIAS_MIN_UPRIGHT_S,
} from '../analysis/config/gyroBias'

const RAD_TO_DEG = 180 / Math.PI

/**
 * Lean rate of a calibrated gyro reading, deg/s, in the lean convention
 * (left positive): the negated bike-frame roll rate about x. Under an
 * untrusted (identity) calibration this is the raw sensor x channel, whose
 * sign is unknowable; the GPS references anchor the fused lean then.
 */
export function leanRateDegS(reading: GyroscopeReading): number {
  return -reading.x * (reading.unit === 'rad/s' ? RAD_TO_DEG : 1)
}

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = sorted.length >> 1
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

/**
 * Constant offset of the calibrated gyro's lean rate, deg/s: the median
 * lean rate over ticks where the vehicle is upright (|GPS lean| below
 * GYRO_BIAS_MAX_LEAN_DEG) and moving, where the true rate is zero. With
 * less than GYRO_BIAS_MIN_UPRIGHT_S of such ticks the whole span's median
 * stands in, and with less than that of ticks altogether the offset is
 * unmeasured (null). Tick time counts up to GYRO_BIAS_MAX_TICK_GAP_MS per
 * tick, so a recording gap is not riding.
 */
export function estimateGyroLeanRateBiasDegS(
  gpsPoints: GpsPoint[],
  gyroPerTick: Array<GyroscopeReading | undefined>,
  gpsLeanDeg: number[]
): number | null {
  const upright: number[] = []
  const all: number[] = []
  let uprightMs = 0
  let allMs = 0
  for (let i = 0; i < gpsPoints.length; i++) {
    const reading = gyroPerTick[i]
    if (!reading) continue
    const rate = leanRateDegS(reading)
    const tickMs =
      i > 0
        ? Math.min(
            GYRO_BIAS_MAX_TICK_GAP_MS,
            gpsPoints[i].timestamp.getTime() -
              gpsPoints[i - 1].timestamp.getTime()
          )
        : 0
    all.push(rate)
    allMs += tickMs
    const lean = gpsLeanDeg[i]
    if (
      Number.isFinite(lean) &&
      Math.abs(lean) < GYRO_BIAS_MAX_LEAN_DEG &&
      gpsPoints[i].speedKmh >= GYRO_BIAS_MIN_SPEED_KMH
    ) {
      upright.push(rate)
      uprightMs += tickMs
    }
  }
  const minMs = GYRO_BIAS_MIN_UPRIGHT_S * 1000
  if (uprightMs >= minMs) return median(upright)
  if (allMs >= minMs) return median(all)
  return null
}
